import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface ProductInput {
  name: string;
  features: string;
  category: string;
}

interface BulkResult {
  productName: string;
  shortDescription: string;
  longDescription: string;
  bullets: string[];
  status: 'success' | 'error';
  error?: string;
}

const BulkImportTab: React.FC = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [csvData, setCsvData] = useState<ProductInput[]>([]);
  const [results, setResults] = useState<BulkResult[]>([]);
  const [targetAudience, setTargetAudience] = useState('General consumers');
  const [tone, setTone] = useState('Professional');

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row, parse CSV
      const products: ProductInput[] = [];
      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(',').map(p => p.trim().replace(/^"|"$/g, ''));
        if (parts.length >= 3) {
          products.push({
            name: parts[0],
            features: parts[1],
            category: parts[2]
          });
        }
      }

      if (products.length === 0) {
        toast.error('No valid products found in CSV');
        return;
      }

      if (products.length > 100) {
        toast.error('Maximum 100 products allowed per batch');
        return;
      }

      setCsvData(products);
      toast.success(`Loaded ${products.length} products from CSV`);
    };
    reader.readAsText(file);
  };

  const handleGenerate = async () => {
    if (csvData.length === 0) {
      toast.error('Please upload a CSV file first');
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      setResults([]);
      toast.loading('Processing products...');

      const userId = await getToken();
      const response = await fetch(`${ENV.API_URL}/api/marketing/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          products: csvData,
          targetAudience,
          tone
        })
      });

      if (!response.ok) throw new Error('Failed to process products');

      // Stream progress if supported, otherwise get all results
      const data = await response.json();
      toast.dismiss();
      setResults(data.results);
      setProgress(100);
      
      const successCount = data.results.filter((r: BulkResult) => r.status === 'success').length;
      toast.success(`Processed ${successCount}/${data.results.length} products successfully!`);
    } catch (error) {
      toast.dismiss();
      console.error('Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to process products');
    } finally {
      setLoading(false);
    }
  };

  const downloadResults = () => {
    const csv = results
      .filter(r => r.status === 'success')
      .map(r => {
        const short = r.shortDescription.replace(/"/g, '""');
        const long = r.longDescription.replace(/"/g, '""');
        const bullets = r.bullets.join('; ').replace(/"/g, '""');
        return `"${r.productName}","${short}","${long}","${bullets}"`;
      })
      .join('\n');

    const header = 'Product Name,Short Description,Long Description,Key Features\n';
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-product-content-${Date.now()}.csv`;
    a.click();
    toast.success('Results exported!');
  };

  const downloadTemplate = () => {
    const template = `Product Name,Features,Category
"Wireless Headphones","Bluetooth 5.0, 30hr battery, noise cancelling","Electronics > Audio"
"Running Shoes","Lightweight mesh, cushioned sole, breathable","Sports > Footwear"
"Coffee Maker","12-cup capacity, programmable timer, auto shutoff","Home > Kitchen"`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-import-template.csv';
    a.click();
    toast.success('Template downloaded!');
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk CSV Import</h3>
        <p className="text-gray-600 text-sm">
          Upload a CSV file with product details to generate descriptions for multiple products at once (max 100 per batch).
        </p>
      </div>

      {results.length === 0 ? (
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Step 1: Upload CSV File</h4>
                <p className="text-sm text-gray-600">
                  CSV must include: Product Name, Features, Category
                </p>
              </div>
              <button
                onClick={downloadTemplate}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Download Template
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-orange-400 transition">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  Click to upload CSV file
                </span>
                <p className="text-xs text-gray-500 mt-1">Max 100 products per batch</p>
              </label>
            </div>

            {csvData.length > 0 && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      ✓ {csvData.length} products loaded
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Ready to process
                    </p>
                  </div>
                  <button
                    onClick={() => setCsvData([])}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Step 2: Configure Options</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <select
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="General consumers">General consumers</option>
                  <option value="Tech enthusiasts">Tech enthusiasts</option>
                  <option value="Professionals">Professionals</option>
                  <option value="Budget shoppers">Budget shoppers</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tone
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="Professional">Professional</option>
                  <option value="Casual">Casual & Friendly</option>
                  <option value="Luxury">Luxury & Premium</option>
                  <option value="Enthusiastic">Enthusiastic</option>
                </select>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading || csvData.length === 0}
            className={`w-full py-4 rounded-lg font-medium text-white transition ${
              loading || csvData.length === 0
                ? 'bg-orange-400 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing {csvData.length} products... ({progress}%)
              </span>
            ) : (
              `Generate Content for ${csvData.length} Products`
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Results Header */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                Processing Complete
              </h4>
              <p className="text-sm text-gray-600">
                {results.filter(r => r.status === 'success').length} of {results.length} products processed successfully
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={downloadResults}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
              >
                Export Results
              </button>
              <button
                onClick={() => {
                  setResults([]);
                  setCsvData([]);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
              >
                Start New Batch
              </button>
            </div>
          </div>

          {/* Results List */}
          <div className="space-y-4">
            {results.map((result, index) => (
              <div
                key={index}
                className={`bg-white rounded-lg border p-5 ${
                  result.status === 'success' ? 'border-gray-200' : 'border-red-300 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-semibold text-gray-900">{result.productName}</h5>
                  {result.status === 'success' ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                      Success
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                      Error
                    </span>
                  )}
                </div>

                {result.status === 'success' ? (
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Short:</span>
                      <p className="text-gray-600 mt-1">{result.shortDescription}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Long:</span>
                      <p className="text-gray-600 mt-1">{result.longDescription.substring(0, 150)}...</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Features:</span>
                      <ul className="mt-1 space-y-1">
                        {result.bullets.slice(0, 3).map((bullet, i) => (
                          <li key={i} className="text-gray-600">• {bullet}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-red-700">{result.error}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImportTab;
